// frontend/packages/business-console/src/pages/Quotes/QuoteLineItemsEditor.jsx
//
// Spreadsheet-row editor for quote / invoice line items. Design doc
// §3.2: "Tab between cells. Add-row button at bottom. Familiar to
// anyone who's used Quickbooks / Stripe / etc. Compresses to a card
// stack on mobile."
//
// Conversion contract:
//   - unitPrice / taxRateBps are stored as integers (cents and bps)
//   - UI converts on the fly: 125.50 USD ⇄ 12550 cents,
//     7.50% ⇄ 750 bps
//   - quantity is stored as a string (backend schema)
//
// Pure-component-ish: parent owns `lineItems` state; this component
// reports edits via onChange(newLineItems).

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Button from '@mui/material/Button';

function newRow(position) {
  return {
    id: `li_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    position,
    description: '',
    quantity: '1',
    unitPrice: 0,
    taxable: false,
    taxRateBps: null,
    lineSubtotal: 0,
    lineTax: 0,
    lineTotal: 0,
  };
}

function parseDollarsToCents(input) {
  const cleaned = String(input || '').replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;
  const num = Number(cleaned);
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

function formatCentsAsDollars(cents) {
  if (cents == null || cents === '') return '';
  const num = (Number(cents) || 0) / 100;
  return num.toFixed(2);
}

function parsePercentToBps(input) {
  const cleaned = String(input || '').replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const num = Number(cleaned);
  if (Number.isNaN(num)) return null;
  return Math.round(num * 100);
}

function formatBpsAsPercent(bps) {
  if (bps == null || bps === '') return '';
  return (Number(bps) / 100).toFixed(2);
}

// A numeric cell that lets you TYPE FREELY. The money/tax fields store
// integers (cents / bps), but reformatting the value on every keystroke
// (toFixed(2)) fought the user: typing "5" into "0.00" became "0.01" and
// the cursor jumped. So we hold the raw text locally while focused, show
// exactly what was typed, commit the parsed integer live (so totals
// update), and only normalize the display on blur.
function NumericTextField({ numericValue, format, parse, onCommit, ...props }) {
  const [text, setText] = useState(() => format(numericValue));
  const focusedRef = useRef(false);

  useEffect(() => {
    // Resync from the model only when NOT editing, so we never reformat
    // (and jump the caret) mid-type.
    if (!focusedRef.current) setText(format(numericValue));
  }, [numericValue, format]);

  return (
    <TextField
      {...props}
      value={text}
      onFocus={(e) => {
        focusedRef.current = true;
        props.onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        onCommit(parse(raw));
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        setText(format(numericValue));
        props.onBlur?.(e);
      }}
    />
  );
}

function computeLineTotal(li) {
  const qty = Number(li.quantity) || 0;
  const sub = qty * (li.unitPrice || 0);
  const tax = li.taxable ? Math.round((sub * (li.taxRateBps || 0)) / 10000) : 0;
  return { lineSubtotal: sub, lineTax: tax, lineTotal: sub + tax };
}

export default function QuoteLineItemsEditor({
  lineItems,
  onChange,
  defaultTaxBps,
  disabled,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lastInputRef = useRef(null);

  const commit = (idx, patch) => {
    const next = lineItems.map((li, i) => {
      if (i !== idx) return li;
      const merged = { ...li, ...patch };
      const totals = computeLineTotal(merged);
      return { ...merged, ...totals };
    });
    onChange(next);
  };

  const addRow = () => {
    onChange([...lineItems, newRow(lineItems.length)]);
  };

  const removeRow = (idx) => {
    const next = lineItems
      .filter((_, i) => i !== idx)
      .map((li, i) => ({ ...li, position: i }));
    onChange(next);
  };

  const moveRow = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= lineItems.length) return;
    const next = [...lineItems];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    onChange(next.map((li, i) => ({ ...li, position: i })));
  };

  if (isMobile) {
    return (
      <Box>
        <Stack spacing={1.5}>
          {lineItems.map((li, idx) => (
            <Box
              key={li.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <TextField
                fullWidth
                multiline
                size="small"
                label="Description"
                value={li.description}
                onChange={(e) => commit(idx, { description: e.target.value })}
                disabled={disabled}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Qty"
                  value={li.quantity}
                  onChange={(e) => commit(idx, { quantity: e.target.value })}
                  disabled={disabled}
                />
                <NumericTextField
                  fullWidth
                  size="small"
                  label="Unit price"
                  numericValue={li.unitPrice}
                  format={formatCentsAsDollars}
                  parse={parseDollarsToCents}
                  onCommit={(cents) => commit(idx, { unitPrice: cents })}
                  disabled={disabled}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1, alignItems: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={Boolean(li.taxable)}
                    onChange={(e) => commit(idx, { taxable: e.target.checked })}
                    disabled={disabled}
                  />
                  <Typography variant="caption">Taxable</Typography>
                </Box>
                <NumericTextField
                  size="small"
                  label="Rate"
                  numericValue={li.taxRateBps}
                  format={formatBpsAsPercent}
                  parse={parsePercentToBps}
                  onCommit={(bps) => commit(idx, { taxRateBps: bps })}
                  disabled={disabled || !li.taxable}
                  placeholder={
                    defaultTaxBps ? formatBpsAsPercent(defaultTaxBps) : '—'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 1.5 }}
              >
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(idx, -1)}
                    disabled={disabled || idx === 0}
                    aria-label="Move up"
                  >
                    <KeyboardArrowUpRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(idx, +1)}
                    disabled={disabled || idx === lineItems.length - 1}
                    aria-label="Move down"
                  >
                    <KeyboardArrowDownRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(idx)}
                    disabled={disabled}
                    aria-label="Remove row"
                    color="error"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary',
                  }}
                >
                  Line {idx + 1}: ${(li.lineTotal / 100).toFixed(2)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={addRow}
          sx={{ mt: 1.5 }}
          disabled={disabled}
        >
          Add line item
        </Button>
      </Box>
    );
  }

  // Desktop: spreadsheet grid
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns:
            '32px minmax(0,1fr) 70px 110px 60px 90px 110px 36px',
          gap: 1,
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box />
        <HeaderCell>Description</HeaderCell>
        <HeaderCell align="right">Qty</HeaderCell>
        <HeaderCell align="right">Unit price</HeaderCell>
        <HeaderCell align="center">Tax</HeaderCell>
        <HeaderCell align="right">Rate</HeaderCell>
        <HeaderCell align="right">Line total</HeaderCell>
        <Box />
      </Box>

      {lineItems.map((li, idx) => {
        const isLast = idx === lineItems.length - 1;
        return (
          <Box
            key={li.id}
            sx={{
              display: 'grid',
              gridTemplateColumns:
                '32px minmax(0,1fr) 70px 110px 60px 90px 110px 36px',
              gap: 1,
              alignItems: 'center',
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack alignItems="center" sx={{ opacity: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => moveRow(idx, -1)}
                disabled={disabled || idx === 0}
                aria-label="Move up"
                sx={{ p: 0 }}
              >
                <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => moveRow(idx, +1)}
                disabled={disabled || idx === lineItems.length - 1}
                aria-label="Move down"
                sx={{ p: 0 }}
              >
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              size="small"
              variant="standard"
              placeholder="Description"
              value={li.description}
              onChange={(e) => commit(idx, { description: e.target.value })}
              disabled={disabled}
              multiline
              maxRows={4}
              inputRef={isLast ? lastInputRef : null}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && isLast) {
                  e.preventDefault();
                  addRow();
                }
              }}
              InputProps={{ disableUnderline: false }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: 13,
                },
              }}
            />
            <TextField
              size="small"
              variant="standard"
              value={li.quantity}
              onChange={(e) => commit(idx, { quantity: e.target.value })}
              disabled={disabled}
              inputProps={{
                inputMode: 'decimal',
                style: {
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                },
              }}
            />
            <NumericTextField
              size="small"
              variant="standard"
              numericValue={li.unitPrice}
              format={formatCentsAsDollars}
              parse={parseDollarsToCents}
              onCommit={(cents) => commit(idx, { unitPrice: cents })}
              disabled={disabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    $
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: 'decimal',
                style: {
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Checkbox
                size="small"
                checked={Boolean(li.taxable)}
                onChange={(e) => commit(idx, { taxable: e.target.checked })}
                disabled={disabled}
              />
            </Box>
            <NumericTextField
              size="small"
              variant="standard"
              numericValue={li.taxRateBps}
              format={formatBpsAsPercent}
              parse={parsePercentToBps}
              onCommit={(bps) => commit(idx, { taxRateBps: bps })}
              disabled={disabled || !li.taxable}
              placeholder={
                defaultTaxBps ? formatBpsAsPercent(defaultTaxBps) : '—'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ ml: 0 }}>
                    %
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: 'decimal',
                style: {
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'right',
                color: 'text.primary',
              }}
            >
              ${(li.lineTotal / 100).toFixed(2)}
            </Typography>
            <Tooltip title="Remove row">
              <IconButton
                size="small"
                onClick={() => removeRow(idx)}
                disabled={disabled || lineItems.length === 1}
                aria-label="Remove row"
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={addRow}
        sx={{ mt: 1 }}
        disabled={disabled}
      >
        Add line item
      </Button>
    </Box>
  );
}

function HeaderCell({ children, align }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: 10,
        textAlign: align || 'left',
      }}
    >
      {children}
    </Typography>
  );
}

export { newRow, computeLineTotal };
